import {
  Component,
  OnInit,
  HostListener,
  ViewChild,
  ElementRef,
  Input,
  forwardRef,
  SimpleChanges,
  OnChanges,
  Output,
  EventEmitter,
} from '@angular/core';
import {
  NG_VALUE_ACCESSOR,
  ControlValueAccessor,
  NG_VALIDATORS,
  AbstractControl,
  ValidationErrors,
} from '@angular/forms';

import * as _ from 'lodash';

export interface AnSelectOptionsInterface {
  selectMultiple?: boolean; // default false
  useKeyValue?: boolean; // default false

  positionFixed?: boolean; // default is false
  inputHeight?: number; // default is 50 px
  inputWidth?: number; // default is 50 px
  filterField?: boolean; // default is false
  filterFieldResetAfterSelect?: boolean; // default is true
  atLeastOnePick?: boolean; // default is false
  openTop?: boolean; // default is false
  closeAfterOneSelected?: boolean; // default is true
}

@Component({
  selector: 'app-an-select',
  templateUrl: './an-select.component.html',
  styleUrls: ['./an-select.component.less'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => AnSelectComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => AnSelectComponent),
      multi: true,
    },
  ],
})
export class AnSelectComponent implements OnInit, OnChanges, ControlValueAccessor {
  @Input() private options: AnSelectOptionsInterface;
  public select_multiple: boolean; // default is false
  public use_key_value: boolean; // default is false
  private position_fixed: boolean;
  public input_height: number; // default is 50
  public input_width: number; // default is 50
  public filter_field: boolean;
  private filter_field_reset_after_select: boolean;
  public at_least_one_pick: boolean;
  public open_top: boolean;
  public close_after_one_selected: boolean;

  @Input() dropdownList: any[]; // dropdownList
  dropdownList_copy: any[]; // this is copy, we used this while searching
  @Input() valuePlaceholder: string;
  @Input() objectKey: string;
  @Input() objectValue: string;
  @Input() hasError: boolean;
  @Input() is_disabled: boolean;
  @Input() slice: boolean;
  @Input() slice_amount: number;
  @Input() with_checkbox: boolean;
  @Input() white: boolean;
  @Input() withoutBorder: boolean;

  valuesSelected: string[] = [];
  valueSelected: string;

  selectDropdown_show = false;
  @ViewChild('selectDropdown')
  selectDropdown: ElementRef;
  @ViewChild('inputElement', { static: true })
  inputElement: ElementRef;
  @ViewChild('inputFilterElement')
  inputFilterElement: ElementRef;
  position = 'down'; // position of dropdown, will open down or up and it depends if there is enough space to open down

  // tslint:disable-next-line: no-output-native
  @Output() change: EventEmitter<any> = new EventEmitter<any>();
  @Output() isDropDownOpen: EventEmitter<any> = new EventEmitter<any>();

  private clicked_times = 0;
  private local_click = false;

  public onTouchCallback: () => void = () => {};
  private onChangeCallback: (_: any) => void = () => {};

  constructor(private list: ElementRef) {}

  ngOnInit() {
    this.options_init();
    this.dropdownList_clone();
  }
  ngOnChanges(changes: SimpleChanges) {
    if (changes) {
      if ('dropdownList' in changes) {
        this.dropdownList_clone();
      }
    }
  }

  // get value from parent [(ngModel)] or formControl
  writeValue(value: string | any) {
    if (value) {
      if (this.select_multiple) {
        this.valuesSelected = _.cloneDeep(value);
        this.dropdownList_clone();
      } else {
        this.valueSelected = value;
        this.dropdownList_clone();
      }
    }
  }
  registerOnChange(fn: any) {
    this.onChangeCallback = fn;
  }
  registerOnTouched(fn: any): void {
    this.onTouchCallback = fn;
  }
  validate(AC: AbstractControl): ValidationErrors | null {
    return;
  }

  private options_init() {
    this.select_multiple = (this.options && this.options.selectMultiple) || false;
    this.use_key_value = (this.options && this.options.useKeyValue) || false;
    this.input_height = (this.options && this.options.inputHeight) || 50;
    this.input_width = (this.options && this.options.inputWidth) || 50;
    this.position_fixed = (this.options && this.options.positionFixed) || false;
    this.filter_field = (this.options && this.options.filterField) || false;
    this.filter_field_reset_after_select = (this.options && this.options.filterFieldResetAfterSelect) || true;
    this.at_least_one_pick = (this.options && this.options.atLeastOnePick) || false;
    this.open_top = (this.options && this.options.openTop) || false;
    this.close_after_one_selected =
      this.options && this.options.closeAfterOneSelected !== undefined ? this.options.closeAfterOneSelected : true;
  }

  public dropdownItem_select(value, e): void {
    e.stopImmediatePropagation();
    if (!this.select_multiple) {
      if (!this.at_least_one_pick) {
        this.valueSelected = this.valueSelected === value ? '' : value;
      } else {
        this.valueSelected = value;
      }
      this.change.emit(this.valueSelected);
      this.onChangeCallback(this.valueSelected);
      this.selectDropdown_close();
      return;
    }
    if (this.valuesSelected.indexOf(value) !== -1) {
      this.valuesSelected.splice(this.valuesSelected.indexOf(value), 1);
      this.onChangeCallback(this.valuesSelected);
      this.selectDropdown_close();
    } else {
      this.valuesSelected.push(value);
      this.onChangeCallback(this.valuesSelected);
      if (this.close_after_one_selected) this.selectDropdown_close();
    }
    if (this.filter_field_reset_after_select) {
      if (this.inputFilterElement) {
        this.inputFilterElement.nativeElement.value = '';
      }
      this.dropdownList_clone();
      if (this.close_after_one_selected) this.selectDropdown_close();
    }
  }

  public dropdownItem_selected(value): boolean {
    if (this.select_multiple) {
      return this.valuesSelected.indexOf(value) !== -1;
    }
    return this.valueSelected === value;
  }

  public dropdownItem_filter(event): void {
    this.dropdownList_clone();
    if (this.use_key_value) {
      this.dropdownList_copy = this.dropdownList_copy.filter(
        (dropdownItem) =>
          dropdownItem[this.objectValue] &&
          dropdownItem[this.objectValue].toLowerCase().includes(event.target.value.toLowerCase()),
      );
    } else {
      this.dropdownList_copy = this.dropdownList_copy.filter((dropdownItem) =>
        dropdownItem.toLowerCase().includes(event.target.value.toLowerCase()),
      );
    }
  }

  private dropdownList_clone(): void {
    const exchenge = [];
    this.dropdownList_copy = _.cloneDeep(this.dropdownList);
    if (this.use_key_value) {
      if (this.select_multiple) {
        this.dropdownList_copy.forEach((el, index) => {
          this.valuesSelected.forEach((value) => {
            if (el[this.objectKey] === value) {
              exchenge.push(this.dropdownList_copy[index]);
              this.dropdownList_copy.splice(index, 1);
            }
          });
        });
        this.dropdownList_copy = [...exchenge, ...this.dropdownList_copy];
      } else {
        if (this.dropdownList_copy) {
          this.dropdownList_copy.forEach((el, index) => {
            if (el[this.objectKey] === this.valueSelected) {
              exchenge.push(this.dropdownList_copy[index]);
              this.dropdownList_copy.splice(index, 1);
            }
          });
        }
        if (this.dropdownList_copy) {
          this.dropdownList_copy = [...exchenge, ...this.dropdownList_copy];
        }
      }
    }
  }

  public selectDropdown_toggle(): void {
    if (!this.is_disabled) {
      this.selectDropdown_show = !this.selectDropdown_show;
      this.isDropDownOpen.emit(this.selectDropdown_show);
      if (!this.selectDropdown_show) {
        this.clicked_times = 0;
      }

      this.scrollIntoView();
      setTimeout(() => {
        if (this.filter_field && this.inputFilterElement) {
          this.inputFilterElement.nativeElement.focus();
        }
      }, 1);
    }
  }
  private selectDropdown_close(): void {
    this.dropdownList_clone();
    this.selectDropdown_show = false;
    this.isDropDownOpen.emit(this.selectDropdown_show);
  }
  private scrollIntoView(): void {
    if ((!this.select_multiple && !this.valueSelected) || (this.select_multiple && this.valuesSelected.length === 0)) {
      return;
    }
    let scrollIndexSum = 0;
    for (const key in this.dropdownList) {
      if (this.dropdownList.hasOwnProperty(key)) {
        if (
          !this.select_multiple
            ? this.dropdownList[key][this.objectKey] !== this.valueSelected
            : this.dropdownList[key][this.objectKey] !== this.valuesSelected[0]
        ) {
          scrollIndexSum++;
        } else {
          break;
        }
      }
    }
    setTimeout(() => {
      if (this.selectDropdown) {
        this.selectDropdown.nativeElement.scrollTop = scrollIndexSum * 24;
      } else {
        this.scrollIntoView();
      }
    }, 16);
  }

  public valuesSelected_get_first(): string {
    if (this.select_multiple) {
      if (this.use_key_value) {
        const found =
          (this.valuesSelected &&
            this.valuesSelected.length &&
            this.dropdownList.find((item) => item[this.objectKey] === this.valuesSelected[0])) ||
          '';
        return found[this.objectValue];
      } else {
        return (this.valuesSelected && this.valuesSelected.length && this.valuesSelected[0]) || '';
      }
    } else {
      if (this.use_key_value) {
        if (this.dropdownList) {
          const found =
            (this.valueSelected && this.dropdownList.find((item) => item[this.objectKey] === this.valueSelected)) || '';
          return found[this.objectValue] || '';
        }
      } else {
        return this.valueSelected;
      }
    }
  }
  public haveValue() {
    return (
      (!this.select_multiple && this.valueSelected && this.valueSelected !== '') ||
      (this.select_multiple && this.valuesSelected && this.valuesSelected.length > 0)
    );
  }
  public valuesSelected_get_sum(): number {
    if (this.select_multiple) {
      const sum = this.valuesSelected && this.valuesSelected.length - 1;
      return sum > 0 ? sum : 0;
    }
  }

  resetSelectedValue(): void {
    if (this.select_multiple) {
      this.valuesSelected = [];
    } else {
      this.valueSelected = null;
    }
  }

  @HostListener('click', ['$event'])
  onLocalClick(event: Event) {
    this.local_click = true;
  }

  @HostListener('document:click', ['$event'])
  onClick(event: any) {
    if (this.close_after_one_selected === false) {
      if (!this.list.nativeElement.contains(event.target) && this.selectDropdown_show) {
        this.selectDropdown_close();
      }
    }
    const dropdown_inner_height = (this.selectDropdown && this.selectDropdown.nativeElement.offsetHeight) || 0;
    const input_bounds = (this.inputElement && this.inputElement.nativeElement.getBoundingClientRect()) || 0;
    // only check this option if date picker is not opend
    this.clicked_times++;
    if (!this.position_fixed && this.clicked_times <= 2) {
      if (window.innerHeight < input_bounds.y + dropdown_inner_height + input_bounds.height) {
        this.position = 'down';
      } else {
        this.position = 'up';
      }
    }
    if (!this.local_click && this.clicked_times >= 2 && this.close_after_one_selected) {
      this.selectDropdown_close();
      this.clicked_times = 0;
    }
    this.local_click = false;
  }
}
